import { isEmpty, isNull, isEqual } from 'lodash';
import { useEffect, useCallback, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { getCurrentWorkspaceSelector } from '@app/reducers/app';
import workspacesReducer from '@app/reducers/entities/workspaces';
import agreementsReducer from '@app/reducers/entities/agreements';
import * as agreementConstants from '@flow/libs/agreement/constants';
import usePrevious from '@app/hooks/use-previous';
import { getValidValue } from '@flow/libs/agreement/sanitizers';

import { useQueryStringParams } from '@app/hocs/query-string-params';
import { useFilterContext } from '@app/hocs/search/filter/context';
import { getFiltersValues } from '@app/hocs/search/filter/utils';
import Filter from '@app/hocs/search/filter/filter';
import { FolderFilter } from '@app/components/all-contracts/components/folder-filter';
import { FolderTreeLegacy } from '@app/components/folder-tree/folder-tree-legacy';
import CheckboxGroup from '@app/components/checkbox-group';
import { lifecycleFilterOptions } from '@app/components/filter-options';
import { checkAcl } from '@flow/libs/utils/acl';
import { T } from '@app/components/translation';

import type { Params } from '@app/hocs/query-string-params/types';
import { useAiInsightsContext } from '@app/contexts/ai-insights-context';
import { stateFilterOptions } from './filter-options/state-filter-options';
import { canSeeInsightContent } from './content/utils';
import { Content } from './content';
import { filterableStates, filterableLifecycles, AI_INSIGHTS_QUERY_AGREEMENTS } from './constants';

import style from './ai-insights.module.scss';
import useClientFeatureFlag from '@app/hooks/use-client-feature-flag';
import { SearchFiltersInformation } from '@app/components/search-filters-information';
import { AllDocumentsLink } from '@app/components/all-contracts/components/all-documents-link';
import { WORKSPACE_ROOT_FOLDER_ID } from '@flow/libs/workspace/folders-constants';

const getSelectedUniqueKey = (params: { folderIds?: number[] } = {}) => {
  if (params?.folderIds) {
    return params.folderIds[0] || -1;
  }

  return -1;
};

const statesFilterSanitizer = (filterableValues: string[]) => (values: string[]) => {
  if (!Array.isArray(values)) {
    return null;
  }
  const filteredValues = values.filter((value: string) =>
    Object.values(filterableValues).includes(value),
  );

  return getValidValue(filteredValues);
};

const isWorkspaceRootFolder = (folderIds: number[] | null) => {
  return folderIds && folderIds.length > 0 && folderIds[0] === WORKSPACE_ROOT_FOLDER_ID;
};

const AiInsights = () => {
  const { subscribe, availableFilters } = useFilterContext();
  const { params, replaceParams } = useQueryStringParams();
  const dispatch = useDispatch();
  const previousParams = usePrevious(params);
  const workspace = useSelector(getCurrentWorkspaceSelector);
  const previousWorkspace = usePrevious(workspace);
  const hasChangedWorkspace = useRef(false);
  const { setInsightsData, workspaceId } = useAiInsightsContext();
  const isFolderV2 = useClientFeatureFlag('folder-v2');

  const hasFoldersViewPermission = checkAcl(workspace.acl, 'collection:folder:view');

  const renderFoldersFilter = () => {
    if (!workspace.virtual && hasFoldersViewPermission) {
      return (
        <Filter
          component={isFolderV2 ? FolderFilter : FolderTreeLegacy}
          defaultValue={[]}
          onSanitize={(value: number[]) => value}
          param="folderIds"
          selectedUniqueKey={getSelectedUniqueKey(params)}
          wrap={false}
          subscribe={subscribe}
          isReadOnly
        />
      );
    }
    return null;
  };

  const executeQuery = useCallback(
    (_params: Params) => {
      const { queryParams } = getFiltersValues(_params, availableFilters.current);

      dispatch(
        workspacesReducer.getAiInsights({
          id: workspaceId,
          data: {
            params: {
              ...queryParams,
              folderIds:
                isFolderV2 && isWorkspaceRootFolder(queryParams.folderIds as number[] | null)
                  ? []
                  : queryParams.folderIds,
              noFolder:
                isFolderV2 && isWorkspaceRootFolder(queryParams.folderIds as number[] | null)
                  ? true
                  : undefined,
            },
          },
          pipe: {
            onSuccess: (data) => {
              setInsightsData(data);
            },
          },
        }),
      );

      const { state, lifecycle, folderIds } = queryParams;

      const capiParams = {
        folderIds:
          isFolderV2 && isWorkspaceRootFolder(folderIds as number[] | null) ? [] : folderIds,
        state: state?.map((_state: string) =>
          Number(
            Object.keys(filterableStates).find(
              (key: string) => filterableStates[Number(key)] === _state,
            ),
          ),
        ),
        lifecycle: lifecycle?.map((_lifecycle: string) =>
          Number(
            Object.keys(filterableLifecycles).find(
              (key: string) => filterableLifecycles[Number(key)] === _lifecycle,
            ),
          ),
        ),
      };
      dispatch(
        agreementsReducer.queryAgreements({
          params: {
            ...capiParams,
            collectionIds: [
              workspaceId,
            ],
            includeFolderSubtree: isEmpty(folderIds) ? 1 : 0,
            noFolder: isFolderV2 && isWorkspaceRootFolder(folderIds as number[] | null) ? 1 : 0,
          },
          name: AI_INSIGHTS_QUERY_AGREEMENTS,
        }),
      );
    },
    [
      availableFilters,
      dispatch,
      workspaceId,
      setInsightsData,
      isFolderV2,
    ],
  );

  useEffect(() => {
    // The first time the component is rendered
    if (isNull(previousParams)) {
      executeQuery(params);
      return;
    }

    // If the current workspace has changed
    if (previousWorkspace && previousWorkspace.id !== workspace.id) {
      // Reset the query params (i.e. deselect folders) when changing workspace
      replaceParams({});
      // Execute the query with empty params, if we pass params, it will be the previous ones since
      // the changes by replaceParams hasn't been applied yet
      executeQuery({});
      hasChangedWorkspace.current = true;
      return;
    }

    // When changing workspaces above, we execute the query, but we also replace the params, which
    // triggers this effect again, so we need to make sure we don't execute the query twice
    if (hasChangedWorkspace.current) {
      hasChangedWorkspace.current = false;
      return;
    }

    const { queryParams: currentQueryParams } = getFiltersValues(params, availableFilters.current);
    const { queryParams: previousQueryParams } = getFiltersValues(
      previousParams || {},
      availableFilters.current,
    );
    // If the query params have changed
    if (!isEqual(previousQueryParams, currentQueryParams)) {
      executeQuery(params);
    }
  }, [
    previousParams,
    params,
    executeQuery,
    availableFilters,
    previousWorkspace,
    workspace,
    replaceParams,
  ]);

  return (
    <div className={style.AiInsights}>
      {canSeeInsightContent(workspace) && (
        <div className={style.FilterSidebarContainer}>
          {isFolderV2 ? (
            <>
              <div className={style.FiltersHeader}>
                <SearchFiltersInformation />
              </div>
              <AllDocumentsLink />
              {renderFoldersFilter()}
            </>
          ) : (
            <>
              {renderFoldersFilter()}
              <h3 className={style.Title}>
                <T
                  _str="Filters"
                  _comment="Header in the filters section."
                />
              </h3>
            </>
          )}
          <Filter
            param="state"
            title="Status"
            onSanitize={statesFilterSanitizer(Object.values(filterableStates))}
            defaultValue={Object.values(filterableStates)}
            options={stateFilterOptions([
              ...Object.keys(filterableStates).map(Number),
              agreementConstants.STATE_DRAFT,
            ]).map((option) => ({
              ...option,
              value: filterableStates[option.value],
            }))}
            component={CheckboxGroup}
            subscribe={subscribe}
            workspace={workspace}
          />
          <Filter
            param="lifecycle"
            type="array"
            title="Lifecycle"
            onSanitize={statesFilterSanitizer(Object.values(filterableLifecycles))}
            defaultValue={[]}
            options={lifecycleFilterOptions(
              ({ id }) => id,
              Object.keys(filterableLifecycles).map(Number),
            ).map((option) => ({
              ...option,
              value: filterableLifecycles[option.value],
            }))}
            component={CheckboxGroup}
            subscribe={subscribe}
            workspace={workspace}
          />
        </div>
      )}
      <Content />
    </div>
  );
};

export default AiInsights;
